<!--  MaterialOrderForm.vue - 등록 및 수정용 --->
<!-- 2026-07-12 한 화면 재구성: 상단 3열(발주대상/일자·수량/공급·입고) + 자재목록이
     스크롤 없이 보이도록 압축. 저장/뒤로가기는 상단 고정. 브랜드/스타일/메인오더는
     선택 — 선구매·도입기 발주 지원 (백엔드 원래 nullable). -->
<template>
  <div class="material-order-form">
    <div class="page-header">
      <h2>{{ isEdit ? '자재 발주서 수정' : '자재 발주서 등록' }}</h2>
      <div class="header-actions">
        <el-button @click="goBack">뒤로가기</el-button>
        <el-button type="primary" @click="submitForm">저장</el-button>
      </div>
    </div>

    <el-form :model="form" :rules="formRules" ref="formRef" label-width="80px" @submit.prevent="submitForm">
      <el-row :gutter="12" class="head-row">
        <!-- ① 발주 대상 — 메인오더 먼저 (선택 시 브랜드/스타일/수량/납기 자동) -->
        <el-col :span="9">
          <el-card shadow="never" class="slim-card">
            <template #header>
              <div class="card-header-slim">발주 대상 <span class="hint">(선택 — 선구매 시 비워두세요)</span></div>
            </template>
            <el-form-item label="메인오더">
              <el-select
                v-model="form.main_request"
                placeholder="선택 시 브랜드/스타일/수량/납기 자동"
                clearable
                filterable
                @change="onMainRequestChange"
              >
                <el-option
                  v-for="mr in mainRequestOptions"
                  :key="mr.id"
                  :label="`${mr.request_number} - ${mr.brand_name} / ${mr.design_style_number}`"
                  :value="mr.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="브랜드" prop="brand">
              <el-select
                v-model="form.brand"
                placeholder="브랜드 선택 (선택사항)"
                clearable
                @change="onBrandChange"
              >
                <el-option
                  v-for="brand in codeStore.brands"
                  :key="brand.id"
                  :label="brand.name"
                  :value="brand.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="스타일">
              <el-select
                v-model="selectedStyleId"
                placeholder="스타일 선택 (선택사항)"
                clearable
                @change="onStyleSelect"
                @clear="onStyleClear"
                :disabled="!styleOptions.length"
              >
                <el-option
                  v-for="style in styleOptions"
                  :key="style.id"
                  :label="`${style.style_number || style.main_style_number} - ${style.designer_name}`"
                  :value="style.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="디자이너">
              <el-input v-model="form.designer" />
            </el-form-item>
          </el-card>
        </el-col>

        <!-- ② 일자·수량 -->
        <el-col :span="7">
          <el-card shadow="never" class="slim-card">
            <template #header>
              <div class="card-header-slim">일자·수량</div>
            </template>
            <el-form-item label="발주일" prop="order_date">
              <el-date-picker v-model="form.order_date" type="date" style="width: 100%" />
            </el-form-item>
            <el-form-item label="납기일" prop="delivery_date">
              <el-date-picker v-model="form.delivery_date" type="date" style="width: 100%" />
            </el-form-item>
            <el-form-item label="기획 수량" prop="request_quantity">
              <el-input-number v-model="form.request_quantity" :min="1" style="width: 100%" />
            </el-form-item>
            <el-form-item label="자재 구분" prop="material_type">
              <el-radio-group v-model="form.material_type">
                <el-radio-button label="raw">원자재</el-radio-button>
                <el-radio-button label="sub">부자재</el-radio-button>
              </el-radio-group>
            </el-form-item>
          </el-card>
        </el-col>

        <!-- ③ 공급·입고 -->
        <el-col :span="8">
          <el-card shadow="never" class="slim-card">
            <template #header>
              <div class="card-header-slim">공급·입고</div>
            </template>
            <!-- 발주법인 (2026-07-15) — 모델에 company 필드는 있었으나 입력 UI 부재로
                 항상 공란이었음. PDF 발행사 정보·법인별 정산의 기준 -->
            <el-form-item label="발주법인">
              <el-select
                v-model="form.company"
                placeholder="법인 선택"
                clearable
              >
                <el-option
                  v-for="c in codeStore.ourCompanys"
                  :key="c.id"
                  :label="c.name"
                  :value="c.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="공급사" prop="supplier">
              <el-autocomplete
                v-model="form.supplier_name"
                :fetch-suggestions="querySearchSupplier"
                placeholder="공급사 또는 사업자 번호 검색"
                @select="onSupplierSelect"
                clearable
                style="width: 100%"
              />
            </el-form-item>
            <div v-if="form.supplier_name" class="supplier-selected">
              ✓ {{ form.supplier_name }} <span class="hint">({{ form.supplier_bn }})</span>
            </div>
            <!-- 발주 통화·적용환율 (2026-07-28) — 구 Access 와 동일하게 헤더에 하나만.
                 항목에는 외화 단가만 입력하고 단가(원화) = 적용환율 × 외화단가 -->
            <el-form-item label="발주 통화">
              <div class="currency-row">
                <el-select v-model="form.currency" style="width: 96px" @change="onCurrencyChange">
                  <el-option v-for="cur in CURRENCY_OPTIONS" :key="cur" :label="cur" :value="cur" />
                </el-select>
                <el-input-number
                  v-if="isForeignOrder"
                  v-model="form.exchange_rate"
                  :min="0"
                  :precision="4"
                  :controls="false"
                  placeholder="적용환율"
                  style="flex: 1"
                  @change="recalcAllForeignPrices"
                />
                <span v-else class="hint">원화 발주</span>
              </div>
            </el-form-item>
            <el-form-item label="입고처" prop="receiving_location">
              <!-- 입고처 = 자사 법인 + 공장(해외 직송) (2026-07-15)
                   해외 자재사→공장 직송 발주는 입고처가 우리 법인이 아니라 공장.
                   (FOB 대사 규칙상 직송도 시스템 발주+공장직송 입고로 기록해야 함) -->
              <el-select v-model="form.receiving_location" placeholder="입고처 선택" filterable>
                <el-option-group label="자사 법인">
                  <el-option
                    v-for="company in codeStore.ourCompanys"
                    :key="'co-' + company.id"
                    :label="company.name"
                    :value="company.name"
                  />
                </el-option-group>
                <el-option-group v-if="factoryWarehouses.length" label="공장 (해외 직송)">
                  <el-option
                    v-for="wh in factoryWarehouses"
                    :key="'wh-' + wh.id"
                    :label="wh.name"
                    :value="wh.name"
                  />
                </el-option-group>
              </el-select>
            </el-form-item>
            <el-form-item label="담당자">
              <el-autocomplete
                v-model="form.order_manager_name"
                :fetch-suggestions="querySearchEmployee"
                placeholder="직원 이름 검색"
                @select="onSelectEmployee"
                clearable
                style="width: 100%"
              />
            </el-form-item>
            <el-form-item label="연락처">
              <el-input v-model="form.order_manager_contact"
                        v-phonemask
                        placeholder="자동 입력" />
            </el-form-item>
          </el-card>
        </el-col>
      </el-row>

      <!-- 색상×사이즈 기획수량 매트릭스 (2026-07-28, 이미영 제보) —
           구 Access `메인색상별수량` 하위 폼과 같은 형태(행=색상, 열=사이즈, 합계 열).
           자재행의 원단색상별 필요량 계산 기준이 되는 값이라 발주 입력 중 늘 보이게 둔다. -->
      <div v-if="mrColors.length || sizeColumns.length" class="qty-matrix-wrap">
        <div class="items-header">
          <h3>색상별 기획수량 <span class="hint">지시서 {{ mainRequestNumber }}</span></h3>
        </div>
        <table class="qty-matrix">
          <thead>
            <tr>
              <th class="c">색상</th>
              <th v-for="s in sizeColumns" :key="s" class="n">{{ s }}</th>
              <th class="n total">합계</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in matrixRows" :key="row.color_name">
              <td class="c">{{ row.color_name }}</td>
              <td v-for="s in sizeColumns" :key="s" class="n">
                {{ row.sizes[s] ? Number(row.sizes[s]).toLocaleString() : '' }}
              </td>
              <td class="n total">{{ Number(row.total).toLocaleString() }}</td>
            </tr>
          </tbody>
          <tfoot v-if="matrixRows.length > 1">
            <tr>
              <td class="c">합계</td>
              <td v-for="s in sizeColumns" :key="s" class="n">
                {{ sizeColumnTotal(s) ? sizeColumnTotal(s).toLocaleString() : '' }}
              </td>
              <td class="n total">{{ matrixGrandTotal.toLocaleString() }}</td>
            </tr>
          </tfoot>
        </table>
        <div v-if="matrixGrandTotal && Number(form.request_quantity) && matrixGrandTotal !== Number(form.request_quantity)"
             class="matrix-warn">
          ⚠ 색상별 합계({{ matrixGrandTotal.toLocaleString() }})가 기획 수량({{ Number(form.request_quantity).toLocaleString() }})과 다릅니다 — 지시서 수량 확인 필요
        </div>
      </div>

      <!-- 자재목록 — 상단 정보 바로 아래, 스크롤 없이 -->
      <div class="items-header">
        <h3>자재목록 <span class="hint" v-if="form.items?.length">{{ form.items.length }}건</span></h3>
        <el-button type="success" size="small" @click="addItem">행 추가</el-button>
        <!-- 지시서 선택 시: 미발주 필요자재 일괄 채우기 (2026-07-14) -->
        <el-button
          v-if="form.main_request && mrMaterials.length"
          type="primary"
          plain
          size="small"
          @click="fillUnorderedMaterials"
        >
          미발주 자재 채우기
        </el-button>
        <span v-if="form.main_request && mrMaterials.length" class="hint">
          지시서 필요자재 {{ mrMaterials.length }}건 — 행별로 연결하면 조달현황에 자동 반영됩니다
        </span>
      </div>
      <el-table :data="form.items || []" style="width: 100%" size="small">
        <!-- 지시서 필요자재 매핑 (2026-07-14) — 이 연결(main_request_material)이
             있어야 발주/입고 수량이 지시서 조달현황 확보율에 역동기화됨 -->
        <el-table-column v-if="form.main_request" label="지시서 자재" width="210">
          <template #default="scope">
            <el-select
              v-model="scope.row.main_request_material"
              placeholder="필요자재 연결"
              size="small"
              clearable
              filterable
              style="width: 100%"
              @change="(val) => onMaterialLink(scope.row, val)"
            >
              <el-option
                v-for="m in mrMaterials"
                :key="m.id"
                :label="matLabel(m)"
                :value="m.id"
              />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="품명">
          <template #default="scope">
            <!-- 자재 마스터 자동완성 (2026-07-15, Phase 2) — 선택 시 코드 연결,
                 목록에 없으면 자유입력 그대로 (마스터 미연결) -->
            <el-autocomplete
              v-model="scope.row.name"
              size="small"
              style="width: 100%"
              :fetch-suggestions="searchMaterialMaster"
              :trigger-on-focus="false"
              placeholder="품명 (마스터 검색)"
              @select="(it) => onMasterSelect(scope.row, it)"
              @input="() => { scope.row.material = null }"
            >
              <template #default="{ item }">
                <span class="mm-code">{{ item.master.code }}</span>
                {{ item.master.name }}
                <span class="hint" v-if="item.master.specification"> · {{ item.master.specification }}</span>
              </template>
            </el-autocomplete>
            <div v-if="scope.row.material" class="mm-linked">✓ 마스터 연결됨</div>
          </template>
        </el-table-column>
        <el-table-column prop="color" label="자재색상" width="100">
          <template #default="scope">
            <el-input v-model="scope.row.color" size="small" placeholder="자재 색" />
          </template>
        </el-table-column>
        <!-- 원단색상 = 이 자재가 투입되는 제품(원단) 색상 (2026-07-28, 구 Access 대조).
             필요량 계산의 색상 매칭 키 — 비우면 전 색상 합계 기준.
             자재색상(위 칸)과 다르다: 검정 원단에 은색 지퍼 → 자재색 은색 / 원단색 검정 -->
        <el-table-column label="원단색상" width="120">
          <template #default="scope">
            <el-select
              v-model="scope.row.fabric_color"
              size="small" clearable filterable allow-create default-first-option
              placeholder="전체 색상"
              style="width: 100%"
            >
              <!-- 옵션 소스는 매트릭스와 동일한 matrixRows (2026-07-28 수정).
                   mrColors(MainRequestColor)만 보면 색상×사이즈(SizeQty)로만
                   수량을 관리하는 지시서에서 목록이 비는 버그 — MKTEST 재현 -->
              <el-option
                v-for="c in matrixRows"
                :key="c.color_name"
                :label="`${c.color_name} (${Number(c.total).toLocaleString()})`"
                :value="c.color_name"
              />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column prop="specification" label="규격" width="110">
          <template #default="scope">
            <el-input v-model="scope.row.specification" size="small" />
          </template>
        </el-table-column>
        <!-- 요척(장당) (2026-07-28, 이미영 제보) — 제품 1장당 소요량.
             입력하면 필요량 = 요척 × 기획수량 자동 계산.
             ⚠ 이 칸과 아래 "필요량"은 서로 다른 값 — 예전 화면의 "요척량" 칸은
             실제로는 총 필요량(required_quantity)이었고, 수기 입력 시에만 장당
             요척으로 쓰이던 혼용 상태였다. -->
        <el-table-column label="요척(장당)" width="95">
          <template #default="scope">
            <el-input-number
              v-model="scope.row.yardage"
              :min="0"
              :precision="4"
              :controls="false"
              size="small"
              style="width: 100%"
              placeholder="장당"
            />
          </template>
        </el-table-column>
        <el-table-column prop="required_quantity" label="필요량" width="95">
          <template #default="scope">
            <el-input v-model="scope.row.required_quantity" size="small"
            :formatter="numberFormatter"
            :parser="numberParser"
            :min="0" />
            <div v-if="Number(scope.row.yardage) && !scope.row.main_request_material" class="calc-hint">
              요척 × {{ Number(matchedPlannedQty(scope.row)).toLocaleString() }}
            </div>
            <div v-else-if="scope.row.main_request_material" class="calc-hint">
              {{ scope.row.fabric_color && !scope.row.mr_color ? '지시서 필요량 × 색상 안분' : '지시서 필요량' }}
            </div>
          </template>
        </el-table-column>
        <!-- 추가수량(LOSS) (2026-07-28, 이미영 제보) — 필요량에 얹는 여유분.
             사유는 비고란에 기재 안내. 저장 대상 아님(발주량에 반영된 값만 저장) -->
        <el-table-column label="추가(LOSS)" width="90">
          <template #default="scope">
            <el-input-number
              v-model="scope.row.loss_quantity"
              :min="0"
              :controls="false"
              size="small"
              style="width: 100%"
              placeholder="0"
            />
          </template>
        </el-table-column>
        <el-table-column prop="order_quantity" label="발주량" width="95">
          <template #default="scope">
            <el-input v-model="scope.row.order_quantity" :min="0" size="small"
            :formatter="numberFormatter"
            :parser="numberParser"
            />
            <!-- 발주량 = 필요량 + 추가(LOSS). 직접 덮어쓸 수 있으나
                 요척/필요량/추가수량을 다시 건드리면 재계산됨. -->
            <div v-if="Number(scope.row.required_quantity)" class="calc-hint">
              필요량 + LOSS
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="unit" label="단위" width="80">
          <template #default="scope">
            <!-- 단위 셀렉트 (2026-07-15, 코드화 검토 §3) — 닫힌 소집합이라
                 셀렉트 우선, 예외는 자유입력(allow-create) -->
            <el-select v-model="scope.row.unit" size="small" filterable
              allow-create default-first-option clearable placeholder="YD">
              <el-option v-for="u in UNIT_OPTIONS" :key="u" :label="u" :value="u" />
            </el-select>
          </template>
        </el-table-column>
        <!-- 외화 단가 (2026-07-28) — 헤더 통화가 외화일 때만 노출.
             단가(원화) = 적용환율 × 외화단가 로 자동 산출 (구 Access 와 동일) -->
        <el-table-column v-if="isForeignOrder" label="단가(외화)" width="105">
          <template #default="scope">
            <el-input-number
              v-model="scope.row.foreign_unit_price"
              :min="0" :precision="4" :controls="false"
              size="small" style="width: 100%"
              :placeholder="form.currency"
              @change="() => applyForeignPrice(scope.row)"
            />
          </template>
        </el-table-column>
        <el-table-column prop="unit_price" label="단가(원)" width="95">
          <template #default="scope">
            <el-input v-model="scope.row.unit_price" size="small"
            :disabled="isForeignOrder"
            :formatter="numberFormatter"
            :parser="numberParser" />
            <div v-if="isForeignOrder" class="calc-hint">환율 × 외화</div>
          </template>
        </el-table-column>
        <el-table-column prop="supply_price" label="공급가" width="100" align="right">
          <template #default="scope">
            <span>{{ numberFormatter(scope.row.supply_price) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="부가세율(%)" width="95">
          <template #default="scope">
            <el-input-number
              v-model="scope.row.vat_rate"
              :min="0"
              :max="100"
              :step="1"
              :controls="false"
              size="small"
              style="width: 100%"
              @change="calculateItemPrices(scope.row)"
            />
          </template>
        </el-table-column>
        <el-table-column prop="vat" label="부가세" width="90" align="right">
          <template #default="scope">
            <span>{{ numberFormatter(scope.row.vat) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="total_price" label="합계" width="100" align="right">
          <template #default="scope">
            <span>{{ numberFormatter(scope.row.total_price) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remarks" label="비고">
          <template #default="scope">
            <el-input v-model="scope.row.remarks" size="small" />
          </template>
        </el-table-column>
        <el-table-column label="" width="70">
          <template #default="scope">
            <el-button type="danger" size="small" link @click="removeItem(scope.$index)">삭제</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-form>
  </div>

  <DesignFormModal ref="designModalRef" @created="onDesignCreated" />

</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axiosInstance from '@/api/axios';
import DesignFormModal from '@/components/DesignFormModal.vue';
//import { ElMessageBox } from 'element-plus'
import { useCodeStore } from "@/stores/codeStore"; // Pinia 스토어 가져오기
import { ElMessage } from 'element-plus';

//Pina 스토어 사용
const codeStore = useCodeStore();
const employeeList = ref([])  // 전체 직원 목록

const form = ref({
  order_manager: null,
  order_manager_name: '',
  order_manager_contact: '',
  supplier:null,
  supplier_bn:null,
  main_request: null,
  // 발주 통화·적용환율 (2026-07-28) — 헤더 단위 (구 Access 적용환율과 동일)
  currency: 'KRW',
  exchange_rate: null,
  items: []
});

const supplierOptions = ref([]);
const mainRequestOptions = ref([]);

// 입고처 공장 옵션 (2026-07-15) — 해외 직송 발주용. 패킹 화면과 동일 API.
const warehouses = ref([]);
const factoryWarehouses = computed(() =>
  warehouses.value.filter(wh => wh.warehouse_type === 'FACTORY')
);
const fetchWarehouses = async () => {
  try {
    const res = await axiosInstance.get('warehouses/', { params: { is_active: true } });
    warehouses.value = res.data.results || res.data || [];
  } catch (err) {
    console.error('창고 목록 조회 실패:', err);
  }
};
const route = useRoute();
const router = useRouter();
const isEdit = route.params.id && route.path.includes('edit');
const designModalRef = ref(null);
const formRef = ref(null);

const formRules = {
  // 브랜드 필수 해제 (2026-07-12) — 스타일 확정 전 선구매/도입기 발주 지원.
  // MaterialOrder.design·main_request 는 모델에서 원래 nullable.
  order_date: [{ required: true, message: '발주일을 입력하세요', trigger: 'change' }],
  delivery_date: [{ required: true, message: '납기일을 입력하세요', trigger: 'change' }],
  request_quantity: [{ required: true, message: '기획수량을 입력하세요', trigger: 'blur' }],
  material_type: [{ required: true, message: '자재구분을 선택하세요', trigger: 'change' }],
  supplier: [{ required: true, message: '공급사를 선택하세요', trigger: 'change' }],
  // 입고처 = 모델 필수 필드(receiving_location)인데 폼 규칙에 빠져 있어
  // 서버 400("이 필드는 필수 항목입니다")으로 튕기던 것 수정 (2026-07-15)
  receiving_location: [{ required: true, message: '입고처를 선택하세요', trigger: 'change' }],
};



//const designSuggestions = ref([]); // 저장용

const selectedStyleId = ref(null);
const styleOptions = ref([]);

const fetchMainRequests = async () => {
  try {
    const res = await axiosInstance.get('/production/main-requests/?ordering=-created_at')
    mainRequestOptions.value = res.data.results || res.data || []
  } catch (err) {
    console.error('메인오더 목록 조회 실패:', err)
  }
}

// ===== 자재 마스터 자동완성 (2026-07-15, Phase 2) =====
// 품명 입력 → 마스터 검색(코드/품명/별칭) → 선택 시 material FK 연결.
// 별칭 검색이 되므로 과거 표기(#5 금속지퍼 등)로 쳐도 표준 자재를 찾는다.
// 단위 표준 목록 (2026-07-15, 코드화 검토) — 의류 자재 닫힌 소집합.
// 예외 단위는 allow-create 자유입력.
const UNIT_OPTIONS = ['YD', 'M', 'EA', 'PCS', 'CONE', 'ROLL', 'KG', 'G', 'SET', 'GROSS', 'DOZEN', '롤', '콘', '타']

const searchMaterialMaster = async (queryString, cb) => {
  if (!queryString || queryString.length < 1) {
    cb([])
    return
  }
  try {
    const res = await axiosInstance.get('/material-masters/search/', {
      params: { q: queryString },
    })
    cb((res.data || []).map(m => ({ value: m.name, master: m })))
  } catch (err) {
    console.error('자재 마스터 검색 실패:', err)
    cb([])
  }
}

const onMasterSelect = (row, item) => {
  const m = item.master
  row.material = m.id
  row.name = m.name
  if (!row.specification) row.specification = m.specification || ''
  if (!row.unit) row.unit = m.unit || ''
  // 공급사 미선택 상태면 기본 공급사 제안
  if (m.default_supplier && !form.value.supplier) {
    const s = supplierOptions.value.find(x => x.supplier_id === m.default_supplier)
    if (s) {
      form.value.supplier = s.supplier_id
      form.value.supplier_name = s.name
      form.value.supplier_bn = s.business_number
    }
  }
}

// ===== 지시서 필요자재 매핑 (2026-07-14) =====
// 발주 행에 main_request_material 을 연결해야 발주/입고 수량이
// 지시서 조달현황(확보율)에 역동기화된다. 기존에는 조달현황 탭 경로
// (query param 진입)에서만 연결됐고, 폼에서 지시서를 수동 선택하면
// 연결이 누락되던 갭을 메움.
const mrMaterials = ref([])

// ===== 컬러별/사이즈별 기획수량 (2026-07-28, 이미영 제보) =====
// 지시서 상세(MainRequestDetailSerializer)에 이미 colors/size_quantities가
// 내려오므로 새 엔드포인트 없이 조회만 추가.
const mrColors = ref([])
const mrSizeQuantities = ref([])

const fetchMrDetail = async (mrId) => {
  if (!mrId) {
    mrColors.value = []
    mrSizeQuantities.value = []
    return
  }
  try {
    const res = await axiosInstance.get(`/production/main-requests/${mrId}/`)
    mrColors.value = res.data.colors || []
    mrSizeQuantities.value = res.data.size_quantities || []
  } catch (err) {
    console.error('메인오더 컬러/사이즈 정보 조회 실패:', err)
    mrColors.value = []
    mrSizeQuantities.value = []
  }
}

// 사이즈 컬럼 목록 — 지시서에 등장한 순서대로 (중복 제거)
const sizeColumns = computed(() => {
  const seen = []
  mrSizeQuantities.value.forEach(s => {
    if (s.size && !seen.includes(s.size)) seen.push(s.size)
  })
  return seen
})

// 색상×사이즈 매트릭스 행 — 색상별 사이즈 분포 + 합계.
// 사이즈 정보가 없는 지시서는 색상 합계(MainRequestColor)만으로도 행을 만든다.
const matrixRows = computed(() => {
  const byColor = new Map()
  mrColors.value.forEach(c => {
    byColor.set(c.color_name, { color_name: c.color_name, sizes: {}, total: Number(c.quantity) || 0 })
  })
  mrSizeQuantities.value.forEach(s => {
    if (!byColor.has(s.color_name)) {
      byColor.set(s.color_name, { color_name: s.color_name, sizes: {}, total: 0 })
    }
    const row = byColor.get(s.color_name)
    row.sizes[s.size] = (row.sizes[s.size] || 0) + (Number(s.quantity) || 0)
  })
  // MainRequestColor 가 없어 total 이 0인 행은 사이즈 합으로 채움
  byColor.forEach(row => {
    if (!row.total) {
      row.total = Object.values(row.sizes).reduce((a, b) => a + b, 0)
    }
  })
  return Array.from(byColor.values())
})

const sizeColumnTotal = (size) =>
  matrixRows.value.reduce((sum, r) => sum + (Number(r.sizes[size]) || 0), 0)

const matrixGrandTotal = computed(() =>
  matrixRows.value.reduce((sum, r) => sum + (Number(r.total) || 0), 0)
)

const mainRequestNumber = computed(() => {
  const mr = mainRequestOptions.value.find(m => m.id === form.value.main_request)
  return mr ? mr.request_number : ''
})

// 필요량 계산의 색상 매칭 키는 **원단색상**(제품 색상)이다 — 자재 자체 색상이
// 아니다. 구 Access `요척_AfterUpdate` 와 동일: 원단색상이 비어 있으면("00")
// 전 색상 합계, 지정돼 있으면 그 색상의 기획수량.
const matchedPlannedQty = (item) => {
  const totalFromMatrix = matrixGrandTotal.value
  const total = totalFromMatrix || Number(form.value.request_quantity) || 0
  const key = (item?.fabric_color || '').trim()
  if (!key) return total
  const hit = matrixRows.value.find(r => (r.color_name || '').trim() === key)
  return hit ? (Number(hit.total) || 0) : total
}

// 계산 체인 (2026-07-28):
//   ① 필요량 산출
//      - 미연결 행 + 요척 입력: 필요량 = 요척 × 기획수량(원단색상 일치분)
//      - 연결 행 + 원단색상 지정: 필요량 = 지시서 필요량 × (색상수량 ÷ 색상합계)
//        — 한 필요자재를 색상별 행으로 쪼개 발주하는 경우. 안분이므로
//        행들의 합 = 지시서 총량이 항상 보존된다 (mr_required_quantity 가 원본).
//      - 연결 행 + 원단색상 미지정: 지시서 필요량 그대로 (재계산 안 함 —
//        지시서는 생산수량(여유율 포함) 기준이라 화면 기획수량(발주수량)으로
//        다시 곱하면 여유율만큼 조용히 줄어든다)
//   ② 발주량 = 필요량 + 추가수량(LOSS)
// 필요량이 0이면 ②는 건너뛴다 (빈 행 보호).
const isLinkedRow = (item) => !!item?.main_request_material

const applyPlannedQtyCalc = (item) => {
  if (!item) return
  if (isLinkedRow(item)) {
    // 안분은 "색상 구분 없는 총량 필요자재를 색상별 행으로 쪼갤 때"만.
    // 색상별로 생성된 필요자재(mr_color 有)는 지시서 필요량이 이미 그 색상
    // 몫 전체라 안분하면 이중 축소된다 (2026-07-28 버그: WHITE 13,200 에
    // 110/1,710 을 또 곱해 849 가 나오던 것) — 지시서 값 그대로 둔다.
    const srcColor = (item.mr_color || '').trim()
    const key = (item.fabric_color || '').trim()
    const baseReq = Number(item.mr_required_quantity) || 0
    const total = matrixGrandTotal.value
    if (!srcColor && key && baseReq > 0 && total > 0) {
      const hit = matrixRows.value.find(r => (r.color_name || '').trim() === key)
      if (hit) {
        item.required_quantity =
          Math.round(baseReq * (Number(hit.total) || 0) / total * 1000) / 1000
      }
    }
    // 그 외 연결 행은 필요량을 건드리지 않음 (지시서 값/수기 존중)
  } else {
    const yd = Number(item.yardage) || 0
    if (yd > 0) {
      const planned = matchedPlannedQty(item)
      // 소수점 3자리까지 유지 (YD 단위 소수 요척 대응)
      item.required_quantity = Math.round(yd * planned * 1000) / 1000
    }
  }
  const req = Number(item.required_quantity) || 0
  if (!req) return
  const loss = Number(item.loss_quantity) || 0
  item.order_quantity = Math.round((req + loss) * 1000) / 1000
}

// ===== 해외 거래처 외화 단가 (2026-07-28, 이미영 제보 / 구 Access 대조) =====
// 통화·환율은 발주서 헤더에 하나. 항목은 외화 단가만 갖고
// 단가(원화) = 적용환율 × 외화단가 로 산출한다.
const CURRENCY_OPTIONS = ['KRW', 'USD', 'CNY', 'JPY', 'EUR']

const isForeignOrder = computed(() => !!form.value.currency && form.value.currency !== 'KRW')

const applyForeignPrice = (item) => {
  if (!isForeignOrder.value) return
  const fp = Number(item.foreign_unit_price) || 0
  const rate = Number(form.value.exchange_rate) || 0
  if (fp > 0 && rate > 0) {
    item.unit_price = Math.round(fp * rate * 100) / 100
    calculateItemPrices(item)
  }
}

const recalcAllForeignPrices = () => {
  (form.value.items || []).forEach(applyForeignPrice)
}

const onCurrencyChange = () => {
  if (!isForeignOrder.value) {
    // 원화로 되돌리면 환율을 비우고 단가는 직접 입력 상태로 (값은 유지)
    form.value.exchange_rate = null
    return
  }
  recalcAllForeignPrices()
}

const fetchMrMaterials = async (mrId) => {
  if (!mrId) {
    mrMaterials.value = []
    return
  }
  try {
    const res = await axiosInstance.get(`production/main-requests/${mrId}/materials/`)
    mrMaterials.value = res.data || []
  } catch (err) {
    console.error('지시서 필요자재 조회 실패:', err)
    mrMaterials.value = []
  }
}

const matLabel = (m) => {
  const parts = [m.item_name]
  if (m.color) parts.push(m.color)
  if (m.specification) parts.push(m.specification)
  const shortage = parseFloat(m.shortage)
  const tail = Number.isFinite(shortage) && shortage > 0
    ? ` (부족 ${shortage})`
    : ' (확보완료)'
  return `[${m.material_type_display || m.material_type}] ${parts.join(' / ')}${tail}`
}

// 행에서 필요자재 선택 시 — 비어 있는 칸만 자동 채움 (입력값 존중)
const onMaterialLink = (row, matId) => {
  if (!matId) return
  const m = mrMaterials.value.find(x => x.id === matId)
  if (!m) return
  if (!row.name) row.name = m.item_name
  if (!row.color) row.color = m.color || ''
  // 색상별 요척에서 내려온 제품 색상 축 (2026-07-28)
  if (!row.fabric_color) row.fabric_color = m.color || ''
  if (!row.specification) row.specification = m.specification || ''
  if (!row.unit) row.unit = m.unit || ''
  // 필요자재에 걸린 마스터 연결을 발주 행으로 전파 (2026-07-15)
  if (!row.material && m.material) row.material = m.material
  // 지시서 필요량 원본·색상 스코프 보관 — 안분 여부 판단 기준 (2026-07-28)
  row.mr_required_quantity = parseFloat(m.required_quantity) || 0
  row.mr_color = m.color || ''
  // 요척(장당)과 필요량(총량)을 각각 제자리에 채운다 (2026-07-28).
  // 예전에는 총 필요량을 "요척량" 칸에 넣어 두 값이 섞여 있었고, 자동계산이
  // 여기에 기획수량을 또 곱해 발주량이 수천 배로 튀는 원인이었다.
  if (!Number(row.yardage) && m.yardage) {
    row.yardage = parseFloat(m.yardage) || null
  }
  if (!Number(row.required_quantity)) {
    row.required_quantity = parseFloat(m.required_quantity) || 0
  }
  if (!Number(row.order_quantity)) {
    row.order_quantity = parseFloat(m.shortage) || parseFloat(m.required_quantity) || 0
  }
}

// 필요자재 → 발주 행 생성 공통 빌더 (2026-07-28) — 일괄 채우기와 행 단위
// 발주(자재현황 [발주] 버튼) 양쪽에서 사용. push 직후 안분/금액 계산까지 수행
// (watcher 는 push 이후 '변경'에만 반응해 초기값엔 계산이 안 걸리는 갭 보완).
const pushRowFromMaterial = (m) => {
  if (!form.value.items) form.value.items = []
  const row = {
    name: m.item_name,
    color: m.color || '',
    // 필요자재의 색상은 색상별 요척에서 내려온 제품 색상 축 — 원단색상에도 채움
    fabric_color: m.color || '',
    specification: m.specification || '',
    unit: m.unit || '',
    material: m.material || null,  // 마스터 연결 전파 (2026-07-15)
    yardage: m.yardage ? (parseFloat(m.yardage) || null) : null,
    mr_color: m.color || '',  // 필요자재의 색상 스코프 — 안분 여부 판단 기준
    mr_required_quantity: parseFloat(m.required_quantity) || 0,
    required_quantity: parseFloat(m.required_quantity) || 0,
    order_quantity: parseFloat(m.shortage) || parseFloat(m.required_quantity) || 0,
    loss_quantity: 0,
    unit_price: 0,
    foreign_unit_price: null,
    supply_price: 0,
    vat_rate: 0,
    vat: 0,
    total_price: 0,
    remarks: '',
    main_request_material: m.id,
  }
  form.value.items.push(row)
  applyPlannedQtyCalc(row)
  calculateItemPrices(row)
  return row
}

// 필요자재의 공급사 → 발주서 공급사 자동 채움 (비어 있을 때만)
const fillSupplierFromMaterial = (m) => {
  if (!m.supplier || form.value.supplier) return
  const s = supplierOptions.value.find(x => x.supplier_id === m.supplier)
  if (s) {
    form.value.supplier = s.supplier_id
    form.value.supplier_name = s.name
    form.value.supplier_bn = s.business_number
  }
}

// 미발주/부분발주 필요자재를 행으로 일괄 추가 (이미 연결된 자재는 제외)
const fillUnorderedMaterials = () => {
  const typeCode = form.value.material_type === 'raw' ? 'RAW'
    : form.value.material_type === 'sub' ? 'ACC' : null
  const linked = new Set(
    (form.value.items || []).map(i => i.main_request_material).filter(Boolean)
  )
  const targets = mrMaterials.value.filter(m =>
    (m.procurement_status === 'NOT_ORDERED' || m.procurement_status === 'PARTIALLY_ORDERED')
    && (!typeCode || m.material_type === typeCode)
    && !linked.has(m.id)
  )
  if (!targets.length) {
    ElMessage.info('추가할 미발주 자재가 없습니다.')
    return
  }
  targets.forEach(pushRowFromMaterial)
  ElMessage.success(`미발주 자재 ${targets.length}건을 추가했습니다.`)
}

const onMainRequestChange = async (mrId) => {
  if (!mrId) {
    form.value.main_request = null
    mrMaterials.value = []
    mrColors.value = []
    mrSizeQuantities.value = []
    // 지시서 해제 시 행의 필요자재 연결도 해제 (다른 지시서 자재가 남는 것 방지)
    let unlinked = 0
    ;(form.value.items || []).forEach(i => {
      if (i.main_request_material) {
        i.main_request_material = null
        unlinked += 1
      }
    })
    if (unlinked) {
      ElMessage.info(`메인오더 해제로 자재 연결 ${unlinked}건을 해제했습니다.`)
    }
    return
  }
  const mr = mainRequestOptions.value.find(m => m.id === mrId)
  await fetchMrMaterials(mrId)
  await fetchMrDetail(mrId)
  if (!mr) return

  // 발주법인 자동 채움 (2026-07-15) — 지시서의 법인 우선,
  // 지시서에 법인이 없으면 브랜드 담당법인(managing_company)으로
  if (mr.company) {
    form.value.company = mr.company
  } else if (mr.brand) {
    const b = codeStore.brands.find(x => x.id === mr.brand)
    if (b?.managing_company) form.value.company = b.managing_company
  }

  // 디자인/브랜드 자동 채움
  if (mr.design) {
    form.value.design = mr.design
    selectedStyleId.value = mr.design
    form.value.style_number = mr.design_style_number
  }
  if (mr.brand) {
    form.value.brand = mr.brand
    // 해당 브랜드의 스타일 옵션도 로드
    await onBrandChange(mr.brand)
    selectedStyleId.value = mr.design
  }
  if (mr.order_quantity) {
    form.value.request_quantity = mr.order_quantity
  }
  if (mr.delivery_date) {
    form.value.delivery_date = mr.delivery_date
  }
}

const onBrandChange = async (brandId) => {
  form.value.brand = brandId;
  selectedStyleId.value = null;
  styleOptions.value = [];
  // 브랜드 해제 시 스타일/디자이너도 초기화 (선구매 발주)
  if (!brandId) {
    onStyleClear();
    return;
  }

  // 브랜드 담당법인 → 발주법인 자동 채움 (2026-07-15, 비어 있을 때만 —
  // 지시서 선택으로 이미 채워진 법인은 존중)
  const brandObj = codeStore.brands.find(x => x.id === brandId)
  if (brandObj?.managing_company && !form.value.company) {
    form.value.company = brandObj.managing_company
  }

  try {
    const res = await axiosInstance.get(`/designs/?brand=${brandId}&ordering=-created_at`);
    styleOptions.value = res.data.results;
  } catch (err) {
    console.error("브랜드 기반 스타일 목록 조회 실패", err);
  }
};

// 발주법인 → 입고처 기본값 (2026-07-15): 입고처가 비어 있을 때만 법인명으로
// 자동 채움. 지시서/브랜드 담당법인 자동 채움 경로까지 전부 반응하도록
// @change 대신 watch 사용 (프로그래매틱 변경은 select @change 미발화).
watch(() => form.value.company, (companyId) => {
  if (!companyId || form.value.receiving_location) return
  const c = codeStore.ourCompanys.find(x => x.id === companyId)
  if (c) form.value.receiving_location = c.name
})

// 스타일 선택 해제 — design 연결 제거 (2026-07-12)
const onStyleClear = () => {
  form.value.design = null;
  form.value.style_number = '';
  form.value.designer = '';
};

const onStyleSelect = async (designId) => {
  const design = styleOptions.value.find(d => d.id === designId);
  if (!design) return;

  form.value.style_number = design.main_style_number;
  form.value.designer = design.designer_name;
  form.value.design = design.id;
};

const loadForm = async () => {
  if (isEdit) {

    const res = await axiosInstance.get(`/material-orders/${route.params.id}/`);
    const items = (res.data.items ?? []).map(item => {
      // 기존 데이터에서 vat_rate 역산 (vat = supply_price * rate/100)
      const sp = Number(item.supply_price) || 0;
      const vatAmt = Number(item.vat) || 0;
      const vatRate = sp > 0 ? Math.round((vatAmt / sp) * 100) : 0;
      // loss_quantity 는 저장 대상이 아닌 화면 보조값 — 기존 발주서 로드 시 0으로 시작
      return { ...item, vat_rate: vatRate, loss_quantity: item.loss_quantity || 0 };
    });
    form.value = { ...res.data, items };

    // 지시서 연결된 발주서면 필요자재 목록 로드 — 매핑 셀렉트 라벨 표시용 (2026-07-14)
    if (form.value.main_request) {
      await fetchMrMaterials(form.value.main_request);
      await fetchMrDetail(form.value.main_request);
      // 연결 행의 지시서 필요량 원본·색상 스코프 백필 (2026-07-28)
      (form.value.items || []).forEach(i => {
        if (i.main_request_material) {
          const m = mrMaterials.value.find(x => x.id === i.main_request_material)
          if (m) {
            i.mr_required_quantity = parseFloat(m.required_quantity) || 0
            i.mr_color = m.color || ''
          }
        }
      })
    }

    // ✅ 디자인 ID가 있다면 해당 디자인 상세 조회 + 브랜드/스타일 옵션 로드
    if (form.value.design) {
      try {
        const designRes = await axiosInstance.get(`/designs/${form.value.design}/`);
        const design = designRes.data;
        form.value.style_number = design.main_style_number;
        form.value.designer = design.designer_name;
        // brand는 ID 유지 (design.brand가 ID)
        if (design.brand) {
          form.value.brand = design.brand;
          await onBrandChange(design.brand);
          selectedStyleId.value = form.value.design;
        }
      } catch (error) {
        console.warn('디자인 정보 조회 실패:', error);
      }
    }

    // ✅ order_manager ID → 이름 매핑
    if (form.value.order_manager && employeeList.value.length) {
      const match = employeeList.value.find(e => e.id === form.value.order_manager);
      if (match) {
        form.value.order_manager_name = match.label;
      }
    }

  }



};

const fetchSuppliers = async () => {
  const res = await axiosInstance.get('/suppliers/');
  supplierOptions.value = Array.isArray(res.data.results) ? res.data.results : [];
};


// 공급사 검색 필터링 함수
const querySearchSupplier = (queryString, cb) => {
  const results = supplierOptions.value.filter(s =>
    s.name.toLowerCase().includes(queryString.toLowerCase()) ||
    s.business_number?.includes(queryString)
  )
  cb(results.map(s => ({
    value: `${s.name} (${s.business_number})`, // 표시용
    id: s.supplier_id,
    name: s.name,
    business_number: s.business_number
  })))
}

// 선택시 ID 저장
const onSupplierSelect = (item) => {
  form.value.supplier = item.id
  form.value.supplier_name = item.name        // 표시용 이름
  form.value.supplier_bn = item.business_number  // 표시용 사업자번호


}


const numberFormatter = (value) => {
  if (value == null || value === '') return '';
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const numberParser = (value) => {
  return value.replace(/,/g, '');
};

const formatDate = (date) => {
    if (!date) return null;
        return new Date(date).toISOString().split('T')[0];  // → YYYY-MM-DD
    };

const calculateItemPrices = (item) => {
  const qty = Number(item.order_quantity) || 0;
  const price = Number(item.unit_price) || 0;
  const vatRate = Number(item.vat_rate) || 0;
  item.supply_price = qty * price;
  item.vat = Math.floor(item.supply_price * (vatRate / 100));
  item.total_price = item.supply_price + item.vat;
};


const submitForm = async () => {
  if (formRef.value) {
    try { await formRef.value.validate() } catch { return }
  }
  if (!form.value.items || form.value.items.length === 0) {
    ElMessage.warning('자재 항목을 1개 이상 추가하세요.');
    return;
  }
  const payload = {
        ...form.value,
        order_date: formatDate(form.value.order_date),
        delivery_date: formatDate(form.value.delivery_date)
        };
  try{

    if (isEdit) {

      await axiosInstance.put(`/material-orders/${route.params.id}/`, payload);
      ElMessage({
                message: "저장 하였습니다.",
                type: "success",
                duration: 3000, // 메시지 표시 시간
              });

    } else {
      const response = await axiosInstance.post('/material-orders/', payload);
      // 상태 흐름 (2026-07-15): 저장 = 작성중 — 확정은 상세 화면에서
      ElMessage.success('작성중으로 저장되었습니다. 상세 화면에서 [결재 상신] 또는 [발주 확정]을 진행하세요.')

      // 생성된 ID를 가져옵니다.
      const newId = response.data.id;

      // 수정 화면으로 이동
      if (newId) {
        router.push(`/material-orders/${newId}`);
      }


    }
  } catch(err)       {
      // 서버 검증 메시지 표시 (2026-07-14) — 예: 입고/출고 이력 있는 항목 삭제 거부
      const data = err.response?.data
      let msg = '발주서 처리 중 오류 발생'
      if (data) {
        const first = data.items ?? data.detail ?? Object.values(data)[0]
        const text = Array.isArray(first) ? first[0] : first
        if (typeof text === 'string') msg = text
      }
      ElMessage.error(msg);
  }


};

const addItem = () => {
  if (!form.value.items) {
    form.value.items = [];
  }

  form.value.items.push({
    material_type: '',
    name: '',
    color: '',
    fabric_color: '',
    specification: '',
    unit: '',
    yardage: null,
    required_quantity: 0,
    order_quantity: 0,
    loss_quantity: 0,
    unit_price: 0,
    foreign_unit_price: null,
    supply_price: 0,
    vat_rate: 0,
    vat: 0,
    total_price: 0,
    remarks: '',
    main_request_material: null,
    material: null
  });
};


const removeItem = (index) => {
  form.value.items.splice(index, 1);
};

// 아이템 항목 자동 계산 처리
const itemWatchers = [];

watch(
  () => form.value.items.length,
  () => {
    // 기존 watcher 해제
    itemWatchers.forEach(stop => stop());
    itemWatchers.length = 0;
    // 각 아이템에 새 watcher 등록
    form.value.items.forEach(item => {
      const stop = watch(
        () => [
          item.order_quantity, item.unit_price, item.vat_rate,
          // 요척/필요량/원단색상/추가수량(LOSS) 변경 시 자동계산 (2026-07-28)
          item.yardage, item.required_quantity, item.fabric_color, item.loss_quantity,
        ],
        () => {
          applyPlannedQtyCalc(item);
          calculateItemPrices(item);
        },
      );
      itemWatchers.push(stop);
    });
  },
  { immediate: true }
);

// 기획수량(전체) 또는 지시서 컬러별 기획수량이 바뀌면 요척 입력된 행 전체 재계산
watch(() => form.value.request_quantity, () => {
  (form.value.items || []).forEach(item => {
    applyPlannedQtyCalc(item);
    calculateItemPrices(item);
  });
});
watch(matrixRows, () => {
  (form.value.items || []).forEach(item => {
    applyPlannedQtyCalc(item);
    calculateItemPrices(item);
  });
});

// 자동완성 검색
const querySearchEmployee = (queryString, cb) => {
  const results = employeeList.value.filter(emp =>
    emp.get_full_name.toLowerCase().includes(queryString.toLowerCase())
  )
  cb(results.map(emp => ({
    value: emp.get_full_name,
    id: emp.id,
    phone: emp.phone_number,
  })))
}

// 선택 시 처리
const onSelectEmployee = (item) => {
  form.value.order_manager = item.id
  form.value.order_manager_name = item.value
  form.value.order_manager_contact = item.phone
}

// 직원 목록 불러오기
const fetchEmployees = async () => {
  const { data } = await axiosInstance.get("/employees");
  employeeList.value = data.map(emp => ({
    ...emp,
    label: emp.get_full_name || emp.first_name + emp.last_name // 검색용
  }));
};

function goBack() {
    history.back();
  }

onMounted( async () => {
  await codeStore.fetchBrands();
  await codeStore.fetchOurCompanys();
  // await 필수 (2026-07-28) — 선택 자재 발주 진입 시 공급사 자동 채움이
  // supplierOptions 로드 완료에 의존 (미대기 시 목록이 비어 매칭 실패 레이스)
  await fetchSuppliers();
  fetchMainRequests();
  fetchWarehouses();
  await fetchEmployees();
  await loadForm();

  // query param으로 메인오더 전달 시 자동 선택
  const mrId = route.query.main_request
  const matType = route.query.material_type // 'raw' or 'sub'
  // 자재현황 탭에서 체크해 넘어온 필요자재 id 목록 (2026-07-28) — 이것만 담는다
  const matIds = route.query.materials
    ? String(route.query.materials).split(',').map(Number).filter(Boolean)
    : []
  if (mrId && !isEdit) {
    form.value.main_request = Number(mrId)
    if (matType) {
      form.value.material_type = matType
    }
    // mainRequestOptions 로드 완료 대기 후 자동 채움
    setTimeout(async () => {
      await onMainRequestChange(Number(mrId))
      if (matIds.length) {
        // 선택 자재만 행으로 + 공급사 자동 채움 (첫 공급사 보유 자재 기준)
        const targets = matIds
          .map(id => mrMaterials.value.find(x => x.id === id))
          .filter(Boolean)
        targets.forEach(pushRowFromMaterial)
        const withSupplier = targets.find(t => t.supplier)
        if (withSupplier) fillSupplierFromMaterial(withSupplier)
        if (targets.length) {
          ElMessage.success(`선택한 자재 ${targets.length}건을 담았습니다.`)
        }
      } else if (matType) {
        // 미발주 자재 자동 채움 — 수동 선택 경로와 동일 로직 사용 (2026-07-14 일원화)
        fillUnorderedMaterials()
      }
    }, 500)
  }
});
</script>

<style scoped>
/* 한 화면 압축 레이아웃 (2026-07-12) — 밀도 규칙은 전역 compact.css,
   여기는 이 화면의 배치(3열 그리드·헤더 액션·자재목록 상단 배치)만 담당 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.header-actions {
  display: flex;
  gap: 8px;
}
.head-row {
  margin-bottom: 8px;
}
.slim-card {
  height: 100%;
}
.slim-card :deep(.el-card__header) {
  padding: 8px 14px;
}
.slim-card :deep(.el-card__body) {
  padding: 10px 14px 2px;
}
.slim-card :deep(.el-form-item) {
  margin-bottom: 10px;
}
.hint {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}
.supplier-selected {
  font-size: 12px;
  color: #67c23a;
  margin: -4px 0 8px 80px;
}
.items-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 6px;
}
.items-header h3 {
  margin: 0;
  font-size: 15px;
}
.mm-code {
  font-size: 11px;
  color: #409eff;
  margin-right: 6px;
  font-family: monospace;
}
.mm-linked {
  font-size: 11px;
  color: #67c23a;
  line-height: 1.2;
}
.calc-hint {
  font-size: 11px;
  color: #909399;
  line-height: 1.3;
}
.currency-row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}
/* 색상×사이즈 기획수량 매트릭스 (구 Access 메인색상별수량 하위 폼과 동일 형태) */
.qty-matrix-wrap {
  margin-bottom: 4px;
}
.qty-matrix {
  border-collapse: collapse;
  font-size: 12px;
}
.qty-matrix th,
.qty-matrix td {
  border: 1px solid #e4e7ed;
  padding: 3px 10px;
  white-space: nowrap;
}
.qty-matrix thead th {
  background: #f5f7fa;
  font-weight: 600;
}
.qty-matrix .c {
  text-align: left;
}
.qty-matrix .n {
  text-align: right;
}
.qty-matrix .total {
  font-weight: 600;
  background: #fafcff;
}
.qty-matrix tfoot td {
  background: #f5f7fa;
  font-weight: 600;
}
.matrix-warn {
  font-size: 12px;
  color: #e6a23c;
  margin-top: 4px;
}
</style>
